import { MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';

interface HierarchyZoomButtonProps {
  /** Direction of zoom: 'in' for child maps, 'out' for parent maps */
  direction: 'in' | 'out';
  /** The shortId of the target map to navigate to */
  targetMapShortId?: string;
}

/**
 * Button component for hierarchy zoom navigation.
 * Displays a magnifying glass icon that navigates to a linked parent or child map.
 * Disabled when no target map is configured.
 */
export function HierarchyZoomButton({ direction, targetMapShortId }: HierarchyZoomButtonProps) {
  const navigate = useNavigate();
  const { userName } = useParams();

  const isDisabled = !targetMapShortId;
  const Icon = direction === 'in' ? MagnifyingGlassPlusIcon : MagnifyingGlassMinusIcon;
  const label = direction === 'in' ? 'Zoom in to child map' : 'Zoom out to parent map';

  const handleClick = () => {
    if (targetMapShortId && userName) {
      navigate(`/${userName}/maps/${targetMapShortId}`);
    }
  };

  // Indent zoom-in buttons to visually distinguish them from zoom-out buttons
  // This helps users understand that parent objectives have zoom-out (no indent)
  // while child objectives have zoom-in (indented)
  const indentClass = direction === 'in' ? 'ml-4' : '';

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={`h-6 w-6 rounded ${indentClass} ${
        isDisabled
          ? 'opacity-30 cursor-not-allowed'
          : 'hover:bg-gray-100 cursor-pointer'
      }`}
      title={isDisabled ? 'No linked map configured' : label}
      aria-label={label}
    >
      <Icon className="h-6 w-6 text-gray-600" aria-hidden="true" />
    </button>
  );
}
